/**
 * @graphql cc
 */

import gql from 'graphql-tag';
import React, { useEffect, useMemo, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import type { ApolloError } from 'apollo-client';
import { defineMessages, useIntl } from 'react-intl-next';

import type { CommentsDataMap as LegacyCommentsDataMap } from '@confluence/comments-data/entry-points/useCommentsData';
import { useCommentsData } from '@confluence/comments-data/entry-points/useCommentsData';
import { updateCommentsDataState } from '@confluence/comments-data/entry-points/commentsDataHelper';
import { useResolvedCommentsQuery } from '@confluence/comments-data/entry-points/useResolvedCommentsQuery';
import {
	useAllCommentsData,
	buildDefaultCommentsContentStatusFilters,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';
import { usePageContentId } from '@confluence/page-context';
import { expVal } from '@confluence/feature-experiments';
import { useUnreadComments } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { useEditorAnnotations } from '@confluence/inline-comments-hooks/entry-points/useEditorAnnotations';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { fg } from '@confluence/feature-gating';
import { SIDEBAR_COMMENTS_PANEL_LOAD_RESOLVED_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext';
import { getVisibleCommentThreads } from '@confluence/comments-panel-utils/entry-points/getVisibleCommentThreads';
import {
	LocationValue,
	getMarkIds,
	getResolvedCommentThreads,
	onlyHasReactionsSummaryErrors,
} from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import {
	ViewValues,
	useCommentsPanel,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import {
	handleCommentsPanelNavigation,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils';

import { usePanelRefresh } from '../hooks/usePanelRefresh';
import { CommentsPanelList } from '../components/CommentsPanelList';
import { Loading } from '../components/Loading';
import { ErrorPanel } from '../components/ErrorPanel';
import { EmptyResolvedSVG } from '../assets/EmptyResolvedSVG';
import { EmptyStateComponent } from '../components/EmptyStateComponent';
import { useCommentsPanelMetrics } from '../hooks/useCommentsPanelMetrics';
import { COMMENTS_PANEL_RESOLVED_VIEW_METRIC } from '../perf.config';

import { GraphQLContentStatus } from './__types__/ResolvedViewResolvedInlineCommentsQuery';
import type {
	ResolvedViewResolvedInlineCommentsQuery as ResolvedViewResolvedInlineCommentsQuery$data,
	ResolvedViewResolvedInlineCommentsQueryVariables as ResolvedViewResolvedInlineCommentsQuery$variables,
} from './__types__/ResolvedViewResolvedInlineCommentsQuery';
import { RESOLVED_TOP_LEVEL_ACTIONS } from './topLevelActions';

const ResolvedViewResolvedInlineCommentsQuery = gql`
	query ResolvedViewResolvedInlineCommentsQuery(
		$pageId: ID!
		$contentStatus: [GraphQLContentStatus!]
	) {
		comments(
			pageId: $pageId
			contentStatus: $contentStatus
			confluenceCommentFilter: { commentState: RESOLVED, commentType: INLINE }
		) @allowHigherTimeout {
			nodes {
				id
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					webui
					editui
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineText
						inlineResolveProperties {
							resolved
							isDangling
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				version {
					by {
						displayName
					}
					when
					number
				}
				author {
					displayName
					profilePicture {
						path
					}
					permissionType
					... on KnownUser {
						accountId
					}
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineText
						inlineResolveProperties {
							isDangling
							resolvedTime
							resolvedFriendlyDate
							resolvedUser
							resolved
						}
					}
				}
				createdAt {
					value
				}
				createdAtNonLocalized
				replies {
					id
					body(representation: ATLAS_DOC_FORMAT) {
						value
					}
					contentStatus
					createdAt {
						value
					}
					createdAtNonLocalized
					links {
						webui
						editui
					}
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineText
							inlineResolveProperties {
								resolved
								isDangling
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
						... on FooterComment {
							commentResolveProperties {
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
					}
					parentId
					permissions {
						isEditable
						isRemovable
						isResolvable
					}
					version {
						by {
							displayName
						}
						when
						number
					}
					author {
						displayName
						profilePicture {
							path
						}
						permissionType
						... on KnownUser {
							accountId
						}
					}
					createdAt {
						value
					}
					createdAtNonLocalized
				}
			}
			totalCount
		}
	}
`;
type ResolvedViewProps = {
	flags?: FlagsStateContainer;
	// Disable loading UI for VR tests
	disabledLoading?: boolean;
};

const i18n = defineMessages({
	emptyCommentsHeaderText: {
		id: 'comments-panel.resolved.view.empty.header.text',
		defaultMessage: 'No resolved comments',
		description:
			'Header text to display in Resolved View empty state when there are no resolved comments on the page.',
	},
	emptyCommentsBodyText: {
		id: 'comments-panel.resolved.view.empty.text',
		defaultMessage:
			'Got comments that aren’t needed? Resolve them! They’ll display here, so you can reopen and add them back in the future.',
		description:
			'Body text to display in Resolved View empty state when there are no resolved comments on the page.',
	},
});

export const ResolvedView = ({ flags, disabledLoading = false }: ResolvedViewProps) => {
	const [loading, setLoading] = useState(true);
	const { formatMessage } = useIntl();

	const [contentId] = usePageContentId();
	const { contentStatus } = useContentStatus();

	const [
		{ commentsDataMap: legacyCommentsDataMap },
		{ addNewCommentThreads, setCommentsDataMap, clearRemovedComments, clearCommentsData },
	] = useCommentsData();
	const [{ readCommentsListState }] = useUnreadComments();
	const [{ annotations }] = useEditorAnnotations();
	const { rendererActions } = useRendererActions();
	const pageMode = useGetPageMode();
	const { userId: currentUserId } = useSessionData();
	const [
		{
			initialDataLoadedForViewMap,
			showInlineComments,
			showGeneralComments,
			currentlySelectedThreadKey,
			commentType,
			currentView,
			commentContainerToFocus,
		},
		{ setInitialDataLoadedForView },
	] = useCommentsPanel();
	const isInitialCommentDataLoaded = initialDataLoadedForViewMap[ViewValues.RESOLVED];
	const { commentService } = useCommentService();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();
	const [allCommentsRendered, setAllCommentsRendered] = useState(false);
	const [commentBatchRendered, setCommentBatchRendered] = useState(false);

	const annotationsInEditorDoc = useMemo(() => {
		return new Set(annotations);
	}, [annotations]);

	// New comments data manager hooks - only used when experiment is enabled
	const newDataManagerResult = useAllCommentsData({
		contentId: contentId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(contentStatus),
		options: {
			skip: !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) || !contentId,
		},
	});

	// Legacy query hooks - only used when experiment is disabled
	/* eslint-disable react-hooks/rules-of-hooks */
	const legacyQueryResult = fg('confluence_frontend_comments_panel_v2')
		? useResolvedCommentsQuery({
				pageId: contentId || '',
				skip: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
			})
		: useQuery<
				ResolvedViewResolvedInlineCommentsQuery$data,
				ResolvedViewResolvedInlineCommentsQuery$variables
			>(ResolvedViewResolvedInlineCommentsQuery, {
				variables: {
					pageId: contentId ?? '',
					contentStatus: [
						GraphQLContentStatus.DRAFT,
						GraphQLContentStatus.CURRENT,
					] satisfies GraphQLContentStatus[],
				},
				skip: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
				onCompleted: (data: ResolvedViewResolvedInlineCommentsQuery$data) => {
					const hasAlreadyFetchedInitialData = Object.keys(legacyCommentsDataMap).length > 0;
					updateCommentsDataState({
						data: data as any, // Just to coerce this type
						readCommentsListState,
						updateData: hasAlreadyFetchedInitialData ? addNewCommentThreads : setCommentsDataMap,
						annotationsInEditorDoc,
						pageMode,
						currentUserId: currentUserId ?? '',
						isOpen: false,
					});
					setLoading(false);
				},
				onError: undefined,
			});
	/* eslint-enable react-hooks/rules-of-hooks */

	// Use the appropriate data source based on experiment
	const {
		loading: hookDataLoading,
		error,
		refetch,
		data,
	} = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? {
				...newDataManagerResult,
				data: undefined,
				error: newDataManagerResult.error as any,
			}
		: legacyQueryResult;

	// Create commentsDataMap from new data when experiment is enabled
	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? (newDataManagerResult.commentsDataMap as LegacyCommentsDataMap)
		: legacyCommentsDataMap;

	// For new data manager, show loading until data processing is complete
	const queryLoading = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? hookDataLoading || !newDataManagerResult.isDataProcessed
		: hookDataLoading;

	useEffect(() => {
		if (error) {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	useEffect(() => {
		if (!fg('confluence_frontend_comments_panel_v2')) {
			if (queryLoading) {
				setLoading(true);
			}
		} else {
			setLoading(queryLoading);
		}
	}, [queryLoading, setLoading]);

	useEffect(() => {
		if (!loading && !isInitialCommentDataLoaded) {
			setInitialDataLoadedForView({ viewToSetLoaded: ViewValues.RESOLVED });

			// handle focused comment
			if (currentlySelectedThreadKey) {
				if (fg('confluence_comments_unified_navigation')) {
					void handleCommentsPanelNavigation({
						scrollBehavior: ScrollBehavior.SMOOTH,
						context: NavigationContext.PANEL,
						block: 'start',
						commentId: commentContainerToFocus || currentlySelectedThreadKey, // TODO: this will be cleaned up later
						commentType: commentType as 'inline' | 'footer' | undefined,
						scrollOnLoad: true,
					});
				} else {
					scrollCommentsPanelToThread({
						annotationId: currentlySelectedThreadKey,
						commentType,
						isCommentThreadResolved: true,
						scrollOnLoad: true,
						commentContainerToFocus,
					});
				}
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps -- ignore change in currentlySelectedThreadKey since we only wan to scroll the first time for focused comments
	}, [loading, setInitialDataLoadedForView]);

	useEffect(() => {
		// When this view unmounts, we want to clear out the "resolved"/"deleted" comments
		return () => {
			clearRemovedComments();
		};
	}, [clearRemovedComments]);

	const { shouldSuppressEmptyState, shouldShowLoading } = usePanelRefresh({
		refetch,
		clearCommentsData,
		registerTriggerCommentPanelRefresh: (handler) =>
			commentService.registerTriggerCommentPanelRefresh(handler),
		queryLoading,
		disabled: disabledLoading,
	});

	const resolvedThreads = useMemo(() => {
		if (!isInitialCommentDataLoaded && loading) {
			return [];
		}

		const orderedAnnotationIds =
			pageMode === PageMode.VIEW ? getMarkIds(rendererActions?.getAnnotationMarks()) : annotations;

		return fg('confluence_frontend_content_wrapper')
			? getVisibleCommentThreads({
					currentView,
					commentsDataMap,
					includeGeneralComments: showGeneralComments,
					includeInlineComments: showInlineComments,
					orderedAnnotationIds,
				})
			: getResolvedCommentThreads({
					commentsDataMap,
					showInlineComments,
					showGeneralComments,
					location: LocationValue.PANEL,
				});
	}, [
		annotations,
		commentsDataMap,
		currentView,
		isInitialCommentDataLoaded,
		loading,
		pageMode,
		rendererActions,
		showGeneralComments,
		showInlineComments,
	]);

	useCommentsPanelMetrics({
		loading,
		error,
		allCommentsRendered,
		commentBatchRendered,
		totalComments: fg('confluence_frontend_abort_panel_exp_if_no_comment')
			? resolvedThreads.length
			: data?.comments?.nodes?.length,
		contentId,
		pageMode,
		experienceName: SIDEBAR_COMMENTS_PANEL_LOAD_RESOLVED_EXPERIENCE,
		metric: COMMENTS_PANEL_RESOLVED_VIEW_METRIC,
	});

	if (shouldShowLoading(isInitialCommentDataLoaded, loading)) {
		return <Loading />;
	}

	if (error && !onlyHasReactionsSummaryErrors(error)) {
		return (
			<ErrorPanel
				error={error as ApolloError}
				onRetryClick={async () => {
					await refetch();
				}}
			/>
		);
	}

	if (resolvedThreads.length === 0 && !shouldSuppressEmptyState()) {
		return (
			<EmptyStateComponent
				SVG={EmptyResolvedSVG}
				emptyCommentsHeaderText={formatMessage(i18n.emptyCommentsHeaderText)}
				emptyCommentsBodyText={formatMessage(i18n.emptyCommentsBodyText)}
				onRendered={
					fg('confluence_frontend_abort_panel_exp_if_no_comment')
						? () => {
								setCommentBatchRendered(true);
								setAllCommentsRendered(true);
							}
						: undefined
				}
			/>
		);
	}

	return (
		<CommentsPanelList
			commentThreads={resolvedThreads}
			supportedTopLevelActions={RESOLVED_TOP_LEVEL_ACTIONS}
			flags={flags}
			onAllCommentsRendered={() => setAllCommentsRendered(true)}
			onCommentBatchRendered={() => setCommentBatchRendered(true)}
		/>
	);
};
